import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ListChecks, LayoutTemplate, Link2, FileDown, FolderOpen, ArrowRight } from 'lucide-react';
import { useTranslation } from 'core/context/TranslationContext';

const FEATURES = [
    { icon: Sparkles, title: 'AI-assisted CV', text: 'Tell us about your background and let AI draft a polished CV you can refine.' },
    { icon: ListChecks, title: 'Step-by-step builder', text: 'Personal info, experience, education, skills, languages, certificates and references, one section at a time.' },
    { icon: LayoutTemplate, title: 'Professional templates', text: 'Choose a template and preview your CV before you download it.' },
    { icon: Link2, title: 'Share with a link', text: 'Send a link so employers can view your CV online.' },
    { icon: FileDown, title: 'Download as PDF', text: 'Export a clean, print-ready PDF whenever you need it.' },
    { icon: FolderOpen, title: 'All CVs in one place', text: 'Keep several versions and manage, edit or delete them from one list.' }
];

const STEPS = [
    { title: 'Sign in', text: 'Use your account, or create one for free.' },
    { title: 'Build your CV', text: 'Start with AI or fill in each section yourself.' },
    { title: 'Share or download', text: 'Send a link or grab the PDF.' }
];

/** Signed-out view of /cv: hero with sign-in, product highlights, and how it works. */
function CVLanding({ onSignIn }) {
    const { _t } = useTranslation();

    return (
        <div className="jf-public jf-cvland">
            <div className="jf-container">
                <section className="jf-cvland__hero">
                    <div className="jf-cvland__copy">
                        <span className="jf-eyebrow"><i />{_t('CV Builder')}</span>
                        <h1>{_t('Build a CV that')} <em>{_t('opens doors')}</em></h1>
                        <p>{_t('Create, share and download professional CVs. Sign in to start building and manage all your CVs in one place.')}</p>
                        <div className="jf-cvland__actions">
                            <button type="button" className="jf-btn jf-btn--primary" onClick={onSignIn}>
                                {_t('Sign in')}
                            </button>
                            <Link to="/signup" className="jf-btn jf-btn--glass">
                                {_t('Create free account')} <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>

                    <div className="jf-cvland__preview" aria-hidden="true">
                        <div className="jf-cvmock">
                            <div className="jf-cvmock__head">
                                <span className="jf-cvmock__avatar" />
                                <div>
                                    <b className="jf-skel" style={{ width: 130 }} />
                                    <b className="jf-skel jf-skel--soft" style={{ width: 90 }} />
                                </div>
                            </div>
                            <b className="jf-skel jf-skel--title" />
                            <b className="jf-skel" style={{ width: '92%' }} />
                            <b className="jf-skel" style={{ width: '78%' }} />
                            <b className="jf-skel jf-skel--title" style={{ marginTop: 14 }} />
                            <b className="jf-skel" style={{ width: '88%' }} />
                            <b className="jf-skel" style={{ width: '64%' }} />
                            <div className="jf-cvmock__chips">
                                <i /><i /><i /><i />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="jf-cvland__section">
                    <div className="jf-section__head">
                        <span className="jf-kicker">{_t('Everything you need')}</span>
                        <h2>{_t('From first draft to finished CV')}</h2>
                    </div>
                    <div className="jf-cvland__grid">
                        {FEATURES.map(({ icon: Icon, title, text }) => (
                            <article className="jf-feature" key={title}>
                                <div className="jf-feature__icon"><Icon size={22} strokeWidth={1.75} /></div>
                                <h3>{_t(title)}</h3>
                                <p>{_t(text)}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="jf-cvland__section jf-cvland__section--last">
                    <div className="jf-cvland__how">
                        <h2>{_t('How it works')}</h2>
                        <ol>
                            {STEPS.map((s, i) => (
                                <li key={s.title}>
                                    <span>{i + 1}</span>
                                    <div><b>{_t(s.title)}</b><small>{_t(s.text)}</small></div>
                                </li>
                            ))}
                        </ol>
                        <button type="button" className="jf-btn jf-btn--primary" onClick={onSignIn}>
                            {_t('Get started')} <ArrowRight size={18} />
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default CVLanding;
